import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { APP_BANNER_HEIGHT, HEADER_Z_INDEX, Icon, Layout, Link, Text } from 'ds';
import { TENANT_ADMIN_PLATFORM_FEE_PROPOSALS_PATH } from 'routes';
import { actions as tenancyActions } from 'store/Tenancy';
import { selectPlatformFees } from 'store/Tenancy/selectors';
import { actions } from 'store/UI';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserV2 } from 'store/v2/User/selectors';
import { isTenantAdminUser } from 'store/v2/User/utils';
import { userCanApproveProposal, userCanViewProposal } from 'ux/Tenant/PlatformFeeProposal/utils';
import { getPlatformFees } from 'ux/Tenant/TenantAdmin/Subscription/requests';

interface Props {}

const TenantBanner: React.FC<Props> = () => {
  const platformFees = useAppSelector(selectPlatformFees);
  const user = useAppSelector(selectUserV2);
  const isTenantAdmin = !!(user && isTenantAdminUser(user));
  const { appBannerType } = useAppSelector(selectUIState);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const platformFeeProposalPath = TENANT_ADMIN_PLATFORM_FEE_PROPOSALS_PATH;
  const isPlatformFeeProposalPage = location.pathname.includes(platformFeeProposalPath);
  useEffect(() => {
    if (!user?.tenancy_id) return;

    getPlatformFees({ tenancyId: user.tenancy_id }).then(({ data: platformFees }) => {
      dispatch(tenancyActions.setPlatformFees(platformFees));
    });
  }, [dispatch, user?.tenancy_id]);

  const getBannerAttributes = () => {
    if (!user) return;

    if (!isTenantAdmin || !platformFees?.length || isPlatformFeeProposalPage) return;

    const platformFeeProposal = platformFees.find(
      pf => !!pf.sent_at && !pf.approved_at && userCanViewProposal(pf, user.id)
    );

    if (!platformFeeProposal) return;

    return {
      type: 'platform_fee_proposal',
      platformFeeProposal,
      canApprove: userCanApproveProposal(platformFeeProposal, user.id)
    } as const;
  };

  const bannerAttributes = getBannerAttributes();

  useEffect(() => {
    if (!bannerAttributes) {
      dispatch(actions.updateUIState({ appBannerType: null }));
    } else {
      dispatch(actions.updateUIState({ appBannerType: bannerAttributes.type }));
    }
  }, [bannerAttributes?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!appBannerType) return null;

  if (!bannerAttributes) return null;

  let content: JSX.Element | null = null;

  if (bannerAttributes.type === 'platform_fee_proposal') {
    content = (
      <Link
        color="black"
        href={`${TENANT_ADMIN_PLATFORM_FEE_PROPOSALS_PATH}/${bannerAttributes.platformFeeProposal.id}`}
        opacity={false}
        fullWidth
      >
        <Layout color="gold-200" height={APP_BANNER_HEIGHT} align="center" justify="center" paddingY={12}>
          <Icon name="clock" stroke={2} color="black" size="xs" />
          <Layout marginLeft={8}>
            <Text size="body-xs" semibold>
              {bannerAttributes.canApprove
                ? 'Your proposal is ready for review'
                : 'Subscription proposal ready for review'}
            </Text>
          </Layout>
          <Layout marginLeft={20}>
            <Text size="body-xs" semibold>
              Review now
            </Text>
          </Layout>
          <Layout marginLeft={4} display="inline-flex">
            <Icon name="rightChevron" size="xs" color="black" />
          </Layout>
        </Layout>
      </Link>
    );
  }

  if (!content) return null;

  return (
    <Layout position="fixed" top={0} left={0} right={0} zIndex={HEADER_Z_INDEX} width="100%">
      {content}
    </Layout>
  );
};

export default TenantBanner;
